import Filter from "./Filter";
import { DatabaseConnectionSchema } from "@avenueops/shared-types";
import { Column, PostgresQueryCompiler, WhereBooleanType, WhereFilter, WhereFilterValueType, WhereOperator } from "@avenueops/visual-query-editor";
import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger, OptionGroup, SmartForm } from "@avenueops/web-components";
import { TrashIcon } from "@heroicons/react/24/outline";
import { FilterIcon } from "components/molecules/filters/FilterIcon";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

const operatorOptions = Object.values(WhereOperator).map((operator) => ({
  value: operator,
  label: operator,
}));

function allowedOperatorsByType(operator: WhereOperator, columnType: WhereFilterValueType) {
  switch (operator) {
    case WhereOperator.BETWEEN:
    case WhereOperator.NOT_BETWEEN:
    case WhereOperator.GREATER_THAN:
    case WhereOperator.GREATER_THAN_OR_EQUAL:
    case WhereOperator.LESS_THAN:
    case WhereOperator.LESS_THAN_OR_EQUAL:
      return columnType === WhereFilterValueType.NUMBER || columnType === WhereFilterValueType.DATE;
    case WhereOperator.LIKE:
    case WhereOperator.NOT_LIKE:
      return columnType === WhereFilterValueType.STRING;
    case WhereOperator.IS_NULL:
    case WhereOperator.IS_NOT_NULL:
    case WhereOperator.EQUALS:
    case WhereOperator.NOT_EQUALS:
      return true;
  }
}

interface Props {
  prefix: string;
  columnOptions: OptionGroup[];
  connectionSchema?: DatabaseConnectionSchema;
}

export default function WhereFiltersCard({ prefix: basePrefix, columnOptions, connectionSchema }: Props) {
  const { setValue, control, watch } = useFormContext();
  const whereFilterFieldArray = useFieldArray({
    name: `${basePrefix}.where.filters`,
    control,
  });

  // The id field is added by the useFieldArray hook and used for the "key" prop
  const whereFilters = useWatch({ name: `${basePrefix}.where.filters`, control }) as (WhereFilter & { id: string })[];

  return (
    <div className="flex gap-2">
      <Collapsible className="bg-white rounded drop-shadow-sm px-4 flex-1" defaultOpen={true}>
        <CollapsibleTrigger>
          <p className="text-sm flex items-center text-gray-500">
            <FilterIcon />
            <span className="ml-2">Filters</span>
          </p>
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-2">
          {whereFilters && whereFilterFieldArray.fields.length > 0
            ? whereFilterFieldArray.fields.map((filter, index) => {
                const prefix = `${basePrefix}.where.filters.${index}`;
                const leftColumn = watch(`${prefix}.left`) as Column;
                const leftColumnType = PostgresQueryCompiler.getTsTypeFromType(leftColumn?.type);
                const filteredOperatorOptions = leftColumnType
                  ? operatorOptions.filter((option) => {
                      return allowedOperatorsByType(option.value, leftColumnType);
                    })
                  : operatorOptions;
                return (
                  <div key={filter.id}>
                    {index > 0 ? <hr className="my-2" /> : null}
                    <div key={filter.id} className="grid grid-cols-5 gap-2">
                      <div className="overflow-hidden col-span-3">
                        <SmartForm.DropdownSelect
                          name={`${prefix}.left.name`}
                          options={columnOptions}
                          onChangeCallback={(val, selectedOption) => {
                            if (selectedOption && selectedOption[0] && selectedOption[0].meta?.data_type) {
                              setValue(`${prefix}.operator`, null);
                              setValue(`${prefix}.left.type`, selectedOption[0].meta.data_type as string);
                              setValue(`${prefix}.left.tableName`, selectedOption[0].meta.tableName as string);
                              setValue(`${prefix}.left.schemaName`, selectedOption[0].meta.schemaName as string);
                              setValue(`${prefix}.left.enumName`, selectedOption[0].meta.enumName as string | undefined);
                              const tsType = PostgresQueryCompiler.getTsTypeFromType(selectedOption[0].meta.data_type as string);
                              setValue(`${prefix}.right.type`, tsType);
                              setValue(`${prefix}.rightSecond.type`, tsType);
                            }
                          }}
                          placeholder="Select Left Column"
                          usePortal
                        />
                      </div>
                      <div className="overflow-hidden col-span-2 flex items-end">
                        <SmartForm.DropdownSelect name={`${prefix}.operator`} hideSearchBar options={filteredOperatorOptions} usePortal />
                        {index > 0 ? (
                          <div className="flex pb-4">
                            <TrashIcon
                              className="mt-2 w-5 h-5 text-gray-100 hover:text-red-600 cursor-pointer"
                              onClick={() => whereFilterFieldArray.remove(index)}
                            />
                          </div>
                        ) : null}
                      </div>
                      <Filter prefix={prefix} columnOptions={columnOptions} connectionSchema={connectionSchema} />
                    </div>
                  </div>
                );
              })
            : null}
          <div className="flex items-center justify-between">
            <div className="flex gap-1 items-center">
              <span>Match</span>
              <SmartForm.DropdownSelect
                name={`${basePrefix}.where.type`}
                hideSearchBar
                usePortal
                defaultValue={WhereBooleanType.AND}
                options={Object.values(WhereBooleanType).map((type) => ({ label: type === WhereBooleanType.AND ? "All Filters" : "Any Filter", value: type }))}
              />
            </div>
            <Button
              type="TEXT"
              onClick={() => {
                whereFilterFieldArray.append({
                  left: "",
                  operator: WhereOperator.EQUALS,
                  right: "",
                });
              }}
            >
              + Add
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <TrashIcon className="mt-2 w-5 h-5 text-gray-500 hover:text-red-600 cursor-pointer" onClick={() => setValue(`${basePrefix}.where`, null)} />
    </div>
  );
}
